import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useLocation, useNavigate } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import { captureEvent } from '@/lib/analytics'
import { streamCoach } from '@pelvia/shared/lib/patient/coachStream'
import { useCoachStarters } from '@pelvia/shared/hooks/patient/useCoachStarters'
import { useProfile } from '@pelvia/shared/hooks/patient/useProfile'
import { initialsFor, patientDisplayName } from '@pelvia/shared/lib/patient/displayName'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder'

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  status?: 'searching' | 'synthesizing' | 'reading_wearable'
  error?: boolean
}

// Open assistant-provided links in a new tab — the coach occasionally cites
// sources, and we don't want the user to lose their chat history.
const markdownComponents = {
  a: ({ href, children, ...rest }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  ),
}

function statusLabel(status: 'searching' | 'synthesizing' | 'reading_wearable'): string {
  if (status === 'searching') return 'Looking into that for you…'
  if (status === 'reading_wearable') return 'Reading your wearable data…'
  return 'Putting it together…'
}

export default function CoachPage() {
  const [threadId, setThreadId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const queryClient = useQueryClient()
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const seedConsumedRef = useRef(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { prompts: starters } = useCoachStarters()
  const { data: user } = useCurrentUser()
  const { data: profileResp } = useProfile()
  // Match the top-bar avatar: prefer the Settings display name and show the
  // same up-to-two-letter initials, so the two icons never disagree.
  const userInitial = initialsFor(
    patientDisplayName({
      displayName: profileResp?.profile?.display_name,
      fullName: user?.full_name,
      email: user?.email,
    }),
  )

  // Deepgram voice input — appends the transcript into the composer so the
  // user can still edit before sending.
  const inputRef2 = useRef(input)
  inputRef2.current = input
  const voice = useVoiceRecorder(
    useCallback((text: string) => {
      setInput((prev) => (prev ? `${prev} ${text}` : text))
    }, []),
  )

  const hasUserMessage = useMemo(() => messages.some((m) => m.role === 'user'), [messages])

  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages])

  // Cancel in-flight stream if the user navigates away mid-answer.
  useEffect(() => () => abortRef.current?.abort(), [])

  // On mount, load any seeded coach opener so it appears as a waiting message.
  // Skipped if a seedPrompt is about to auto-send (different flow).
  useEffect(() => {
    const hasSeed = !!(location.state as { seedPrompt?: string } | null)?.seedPrompt?.trim()
    if (hasSeed || threadId || messages.length > 0) return
    let cancelled = false
    void (async () => {
      try {
        const data = await api.get<{
          thread_id: string | null
          messages: Array<{ message_id: number; role: string; content: string }>
          unread: boolean
        }>('/api/patient/coach/latest-thread')
        if (cancelled || !data.thread_id || data.messages.length === 0) return
        setThreadId(data.thread_id)
        setMessages(
          data.messages.map((m) => ({
            id: String(m.message_id),
            role: m.role as 'user' | 'assistant',
            content: m.content,
          })),
        )
        if (data.unread) {
          await api.post('/api/patient/coach/mark-read', { thread_id: data.thread_id })
          queryClient.invalidateQueries({ queryKey: ['coach-unread'] })
        }
      } catch {
        // Non-critical — empty coach is fine
      }
    })()
    return () => { cancelled = true }
  // Run once on mount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Consume a seed prompt passed via navigation state (e.g. from a pattern or
  // condition card on Briefing/Pattern pages). Auto-send once, then strip the
  // state so back-forward navigation doesn't replay the message.
  useEffect(() => {
    if (seedConsumedRef.current) return
    const state = location.state as { seedPrompt?: string } | null
    const seed = state?.seedPrompt?.trim()
    if (!seed) return
    seedConsumedRef.current = true
    navigate(location.pathname, { replace: true, state: null })
    void send(seed)
    // send is stable enough for our needs; we only want this to fire on mount-
    // adjacent renders when state arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  const ensureThread = useCallback(async (): Promise<string | null> => {
    if (threadId) return threadId
    try {
      const data = await api.post<{ thread_id: string }>('/create-thread', {})
      setThreadId(data.thread_id)
      return data.thread_id
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        window.location.href = '/login'
      }
      return null
    }
  }, [threadId])

  const send = useCallback(
    async (raw: string) => {
      const message = raw.trim()
      if (!message || sending) return

      setInput('')
      setSending(true)
      captureEvent('coach_message_sent')

      const userMsg: ChatMessage = {
        id: `u-${Date.now()}`,
        role: 'user',
        content: message,
      }
      const assistantId = `a-${Date.now()}`
      const assistantMsg: ChatMessage = { id: assistantId, role: 'assistant', content: '' }
      setMessages((prev) => [...prev, userMsg, assistantMsg])

      const tid = await ensureThread()
      if (!tid) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: 'Could not start a chat thread. Please try again.', error: true }
              : m,
          ),
        )
        setSending(false)
        return
      }

      // Snapshot the prior chat history so the stream request reflects the
      // state *before* the just-added user message (server expects turn pairs).
      const history = messages
        .filter((m) => !m.error && (m.role === 'user' || (m.role === 'assistant' && m.content)))
        .map((m) => ({ role: m.role, content: m.content }))

      const controller = new AbortController()
      abortRef.current?.abort()
      abortRef.current = controller

      let accumulated = ''
      let errored = false

      try {
        for await (const event of streamCoach({
          message,
          chatHistory: history,
          threadId: tid,
          signal: controller.signal,
        })) {
          if (event.type === 'status') {
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, status: event.status } : m)),
            )
          } else if (event.type === 'text') {
            accumulated += event.text
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, content: accumulated, status: undefined } : m,
              ),
            )
          } else if (event.type === 'error') {
            errored = true
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, content: event.message, error: true, status: undefined } : m,
              ),
            )
          } else if (event.type === 'complete') {
            // Server signalled end-of-stream explicitly.
            break
          }
        }
        if (!accumulated && !errored) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: 'No response received. Please try again.', error: true, status: undefined }
                : m,
            ),
          )
        }
      } finally {
        setSending(false)
        if (abortRef.current === controller) abortRef.current = null
      }
    },
    [ensureThread, messages, sending],
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send(input)
    }
  }

  const autosize = () => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(140, el.scrollHeight)}px`
  }

  return (
    <div className="coach-page">
      <header className="coach-page-header">
        <div className="coach-avatar-badge" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        <div className="coach-header-text">
          <h2>AI Coach</h2>
          <p>Your personal pelvic pain companion.</p>
        </div>
      </header>

      <div className="coach-scroll" ref={scrollRef}>
        <div className="coach-disclaimer">
          General wellness information, not medical advice. For anything urgent, contact your healthcare provider.
        </div>

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`coach-msg coach-msg-${msg.role}${msg.error ? ' coach-msg-error' : ''}`}
          >
            {msg.role === 'assistant' && (
              <div className="coach-msg-avatar coach-msg-avatar-assistant" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
            )}
            <div className="coach-msg-bubble">
              {msg.role === 'assistant' && !msg.content && msg.status && (
                <div className="coach-typing">
                  <span className="coach-typing-dot" />
                  <span className="coach-typing-dot" />
                  <span className="coach-typing-dot" />
                  <span className="coach-typing-label">{statusLabel(msg.status)}</span>
                </div>
              )}
              {msg.role === 'assistant' && !msg.content && !msg.status && (
                <div className="coach-typing">
                  <span className="coach-typing-dot" />
                  <span className="coach-typing-dot" />
                  <span className="coach-typing-dot" />
                </div>
              )}
              {msg.content &&
                (msg.role === 'assistant' ? (
                  <div className="coach-msg-md">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  msg.content
                ))}
            </div>
            {msg.role === 'user' && (
              <div className="coach-msg-avatar coach-msg-avatar-user" aria-hidden>
                {userInitial}
              </div>
            )}
          </div>
        ))}

        {!hasUserMessage && starters.length > 0 && (
          <div className="coach-starters">
            <div className="coach-starters-label">Suggested for you</div>
            <div className="coach-starters-grid">
              {starters.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className="coach-starter-chip"
                  onClick={() => send(prompt)}
                  disabled={sending}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={`coach-composer${input.includes('\n') ? ' has-multiline' : ''}`}>
        <textarea
          ref={inputRef}
          className="coach-composer-input"
          placeholder="Message your coach…"
          value={input}
          rows={1}
          onChange={(e) => {
            setInput(e.target.value)
            autosize()
          }}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />
        {voice.supported && (
          <button
            type="button"
            className={`coach-composer-mic${voice.isRecording ? ' recording' : ''}`}
            onClick={voice.toggle}
            disabled={sending || voice.isTranscribing}
            aria-label={voice.isRecording ? 'Stop recording' : 'Dictate message'}
            aria-pressed={voice.isRecording}
          >
            {voice.isTranscribing ? (
              <span className="loading-spinner" aria-hidden />
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            )}
          </button>
        )}
        <button
          type="button"
          className="coach-composer-send"
          onClick={() => send(input)}
          disabled={sending || !input.trim()}
          aria-label="Send message"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14" />
            <path d="M13 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      {voice.error && (
        <div className="coach-voice-error" role="alert">{voice.error}</div>
      )}
    </div>
  )
}
